import { useCallback, useMemo } from "react";
import { IWebsiteBuilderClipboard } from "../clipboard/useWebsiteBuilderClipboard";
import {
  getSectionNextZIndex,
  TWebsiteBuilderSection,
  WEBSITE_BUILDER_SECTION_CHILD_PLACEMENT_BOUNDARY as SECTION_CHILD_PLACEMENT_BOUNDARY,
} from "../../nodes/section/websiteBuilderSection";
import { isWebsiteBuilderCopyableNode } from "../../nodes/websiteBuilderNode";
import {
  websiteBuilderAddNode,
  websiteBuilderDeleteNode,
  websiteBuilderMoveSection,
  websiteBuilderSelectNode,
} from "redux/actions/websiteBuilder/websiteBuilder";
import { IWebsiteBuilderRoot } from "../../nodes/websiteBuilderRoot";
import { clamp, cloneDeep } from "lodash";
import { useAppDispatch } from "hooks/redux";
import { TWebsiteBuilderTreeRefMap } from "../treeRefMap/useTreeRefMapRegistration";
import {
  getElementPosition,
  isWebsiteBuilderStretchElementData,
  TWebsiteBuilderElement,
} from "../../nodes/element/websiteBuilderElement";
import { TWebsiteBuilderScrollContainerRef } from "../../WebsiteBuilderEditor";

type TWebsiteBuilderMoveSectionUp = (
  node: TWebsiteBuilderSection,
  parentNode: IWebsiteBuilderRoot,
  treeRefMap: TWebsiteBuilderTreeRefMap,
  scrollContainerRef: TWebsiteBuilderScrollContainerRef,
) => void;
type TWebsiteBuilderMoveSectionDown = (
  node: TWebsiteBuilderSection,
  parentNode: IWebsiteBuilderRoot,
  treeRefMap: TWebsiteBuilderTreeRefMap,
  scrollContainerRef: TWebsiteBuilderScrollContainerRef,
) => void;
type TWebsiteBuilderCopySection = (node: TWebsiteBuilderSection) => void;
type TWebsiteBuilderPasteAtSection = (
  targetNode: TWebsiteBuilderSection,
  targetParentNode: IWebsiteBuilderRoot,
  leftX: number,
  treeRefMap: TWebsiteBuilderTreeRefMap,
  scrollContainerRef: TWebsiteBuilderScrollContainerRef,
) => void;
type TWebsiteBuilderDuplicateSection = (
  node: TWebsiteBuilderSection,
  parentNode: IWebsiteBuilderRoot,
  treeRefMap: TWebsiteBuilderTreeRefMap,
) => void;
type TWebsiteBuilderDeleteSection = (node: TWebsiteBuilderSection, parentNode: IWebsiteBuilderRoot) => void;
type TWebsiteBuilderCutSection = (node: TWebsiteBuilderSection, parentNode: IWebsiteBuilderRoot) => void;

export interface IWebsiteBuilderSectionOperations {
  moveSectionUp: TWebsiteBuilderMoveSectionUp;
  moveSectionDown: TWebsiteBuilderMoveSectionDown;
  copySection: TWebsiteBuilderCopySection;
  pasteAtSection: TWebsiteBuilderPasteAtSection;
  duplicateSection: TWebsiteBuilderDuplicateSection;
  deleteSection: TWebsiteBuilderDeleteSection;
  cutSection: TWebsiteBuilderCutSection;
}

export function useWebsiteBuilderSectionOperations(
  clipboard: IWebsiteBuilderClipboard,
): IWebsiteBuilderSectionOperations {
  const dispatch = useAppDispatch();

  const moveSectionUp: TWebsiteBuilderMoveSectionUp = useCallback(
    (node, parentNode, treeRefMap, scrollContainerRef) => {
      const prevScrollTop = scrollContainerRef.current?.scrollTop;

      const index = parentNode.children.findIndex(child => child.id === node.id);

      if (index === -1 || index === 0) {
        return;
      }

      dispatch(
        websiteBuilderMoveSection({
          id: node.id,
          index,
          direction: "up",
        }),
      );

      requestAnimationFrame(() => {
        const sectionRef = treeRefMap.current.get(node.id);

        if (sectionRef && scrollContainerRef.current && prevScrollTop != null) {
          scrollContainerRef.current.scrollTo({ top: prevScrollTop });

          sectionRef.scrollIntoView({ behavior: "smooth", block: "center" });
        }
      });
    },
    [],
  );

  const moveSectionDown: TWebsiteBuilderMoveSectionDown = useCallback(
    (node, parentNode, treeRefMap, scrollContainerRef) => {
      const prevScrollTop = scrollContainerRef.current?.scrollTop;

      const index = parentNode.children.findIndex(child => child.id === node.id);

      if (index === -1 || index === parentNode.children.length - 1) {
        return;
      }

      dispatch(
        websiteBuilderMoveSection({
          id: node.id,
          index,
          direction: "down",
        }),
      );

      requestAnimationFrame(() => {
        const sectionRef = treeRefMap.current.get(node.id);

        if (sectionRef && scrollContainerRef.current && prevScrollTop != null) {
          scrollContainerRef.current.scrollTo({ top: prevScrollTop });

          sectionRef.scrollIntoView({ behavior: "smooth", block: "center" });
        }
      });
    },
    [],
  );

  const copySection: TWebsiteBuilderCopySection = useCallback(
    node => {
      if (isWebsiteBuilderCopyableNode(node)) {
        clipboard.copy(node);
      }
    },
    [clipboard],
  );

  const pasteAtSection: TWebsiteBuilderPasteAtSection = useCallback(
    (targetNode, targetParentNode, leftX, treeRefMap, scrollContainerRef) => {
      const copiedNode = clipboard.peek();

      if (copiedNode?.type === "element") {
        const nodeToPaste: TWebsiteBuilderElement = cloneDeep(copiedNode);

        nodeToPaste.element.zIndex = getSectionNextZIndex(targetNode);

        const sectionRect = treeRefMap.current.get(targetNode.id)?.getBoundingClientRect();
        const containerRect = scrollContainerRef.current?.getBoundingClientRect();

        if (!sectionRect || !containerRect) {
          return;
        }

        const nodeToPasteWidth = nodeToPaste.element.dimensions.width;
        const nodeToPasteHeight = nodeToPaste.element.dimensions.height;

        const position = getElementPosition(leftX, {
          x: sectionRect.width / 2 - nodeToPasteWidth / 2,
          y: sectionRect.height / 2 - nodeToPasteHeight / 2,
        });

        if (
          position.y < SECTION_CHILD_PLACEMENT_BOUNDARY ||
          position.y + nodeToPasteHeight > sectionRect.height - SECTION_CHILD_PLACEMENT_BOUNDARY
        ) {
          return;
        }

        const stretchedElement =
          isWebsiteBuilderStretchElementData(nodeToPaste.element) && nodeToPaste.element.stretch.enabled;

        if (stretchedElement) {
          position.x = 0;
        }

        const containerTopWithBoundary = containerRect.top + SECTION_CHILD_PLACEMENT_BOUNDARY;
        const containerBottomWithBoundary = containerRect.bottom - SECTION_CHILD_PLACEMENT_BOUNDARY;

        const screenPositionTop = sectionRect.top + position.y;
        const screenPositionBottom = screenPositionTop + nodeToPasteHeight;

        if (screenPositionTop < containerTopWithBoundary) {
          const delta = containerTopWithBoundary - screenPositionTop;
          position.y += delta;
        } else if (screenPositionBottom > containerBottomWithBoundary) {
          const delta = screenPositionBottom - containerBottomWithBoundary;
          position.y -= delta;
        }

        position.y = clamp(
          position.y,
          SECTION_CHILD_PLACEMENT_BOUNDARY,
          sectionRect.height - nodeToPasteHeight - SECTION_CHILD_PLACEMENT_BOUNDARY,
        );

        nodeToPaste.element.position.x = position.x;
        nodeToPaste.element.position.y = position.y;

        dispatch(
          websiteBuilderAddNode({
            snapshot: {
              parentId: targetNode.id,
              node: nodeToPaste,
              index: targetNode.children.length,
            },
          }),
        );

        dispatch(
          websiteBuilderSelectNode({
            selectedNode: {
              parentId: targetNode.id,
              id: nodeToPaste.id,
              editingMode: "none",
            },
          }),
        );
      } else if (copiedNode?.type === "section") {
        const nodeToPaste: TWebsiteBuilderSection = cloneDeep(copiedNode);

        const targetNodeType = targetNode.section.type;

        let index: number = null;

        if (targetNodeType === "header") {
          index = 0;
        } else if (targetNodeType === "footer") {
          index = targetParentNode.children.length;
        } else {
          const nodeIndex = targetParentNode.children.findIndex(child => child.id === targetNode.id);

          if (nodeIndex === -1) {
            return;
          }

          index = nodeIndex + 1;
        }

        const prevScrollTop = scrollContainerRef.current?.scrollTop;

        dispatch(
          websiteBuilderAddNode({
            snapshot: {
              parentId: targetParentNode.id,
              node: nodeToPaste,
              index,
            },
          }),
        );

        dispatch(
          websiteBuilderSelectNode({
            selectedNode: {
              parentId: targetParentNode.id,
              id: nodeToPaste.id,
              editingMode: "none",
            },
          }),
        );

        requestAnimationFrame(() => {
          const pastedNodeRef = treeRefMap.current.get(nodeToPaste.id);

          if (pastedNodeRef && scrollContainerRef.current && prevScrollTop != null) {
            const layoutShift = targetNodeType === "footer" ? pastedNodeRef.getBoundingClientRect().height : 0;

            scrollContainerRef.current.scrollTo({ top: prevScrollTop + layoutShift });

            setTimeout(() => {
              pastedNodeRef.scrollIntoView({ behavior: "smooth", block: "center" });
            }, 200);
          }
        });
      }
    },
    [clipboard],
  );

  const duplicateSection: TWebsiteBuilderDuplicateSection = useCallback((node, parentNode, treeRefMap) => {
    const nodeType = node.section.type;
    const nodeIndex = parentNode.children.findIndex(child => child.id === node.id);

    if (nodeType === "header" || nodeType === "footer" || nodeIndex === -1) {
      return;
    }

    const nodeToDuplicate = cloneDeep(node);

    nodeToDuplicate.id = crypto.randomUUID();

    nodeToDuplicate.children.forEach(sectionChild => {
      sectionChild.id = crypto.randomUUID();

      if (sectionChild.type === "container") {
        sectionChild.children.forEach(containerChild => {
          containerChild.id = crypto.randomUUID();
        });
      }
    });

    const index = nodeIndex + 1;

    dispatch(
      websiteBuilderAddNode({
        snapshot: {
          parentId: parentNode.id,
          node: nodeToDuplicate,
          index,
        },
      }),
    );

    dispatch(
      websiteBuilderSelectNode({
        selectedNode: {
          parentId: parentNode.id,
          id: nodeToDuplicate.id,
          editingMode: "none",
        },
      }),
    );

    requestAnimationFrame(() => {
      const duplicatedNodeRef = treeRefMap.current.get(nodeToDuplicate.id);

      if (duplicatedNodeRef) {
        setTimeout(() => {
          duplicatedNodeRef.scrollIntoView({ behavior: "smooth", block: "center" });
        }, 200);
      }
    });
  }, []);

  const deleteSection: TWebsiteBuilderDeleteSection = useCallback((node, parentNode) => {
    const index = parentNode.children.findIndex(child => child.id === node.id);

    if (index === -1) {
      return;
    }

    dispatch(
      websiteBuilderDeleteNode({
        snapshot: {
          parentId: parentNode.id,
          node: cloneDeep(node),
          index,
        },
      }),
    );
  }, []);

  const cutSection: TWebsiteBuilderCutSection = useCallback(
    (node, parentNode) => {
      copySection(node);
      deleteSection(node, parentNode);
    },
    [copySection, deleteSection],
  );

  const sectionOperations: IWebsiteBuilderSectionOperations = useMemo(
    () => ({
      moveSectionUp,
      moveSectionDown,
      copySection,
      pasteAtSection,
      duplicateSection,
      deleteSection,
      cutSection,
    }),
    [moveSectionUp, moveSectionDown, copySection, pasteAtSection, duplicateSection, deleteSection, cutSection],
  );

  return sectionOperations;
}
